// "Tu escalera hoy": lo que el vendedor llevaría en el corte con cada venta adicional, con las reglas
// vigentes y el corte en curso de `GET /commissions/me`. Cada venta adicional se cuenta como un equipo
// (una pieza); lo que depende de qué o a qué precio vende no entra a la curva y se dice.
import type { RetailCommission } from '@fiado/type-kit';
import { CommissionBonusModeEnum } from '@fiado/type-kit/bin/retailCommission/enums/CommissionBonusModeEnum';
import { CommissionMetricEnum } from '@fiado/type-kit/bin/retailCommission/enums/CommissionMetricEnum';
import { CommissionOperatorEnum } from '@fiado/type-kit/bin/retailCommission/enums/CommissionOperatorEnum';
import { factor, pesos } from '@/domains/retail/admin/comisiones';
import { countsSales, isCutRule, metricNow, nextGoalOf } from './myCommissionsView';

type Week = RetailCommission.CommissionSellerBreakdown;
type RuleView = RetailCommission.CommissionRuleView;
type Condition = RetailCommission.CommissionRuleCondition;

const { POSICION_VENTA_EN_PERIODO, COLOCACION_PERIODO, SKUS_DISTINTOS_PERIODO, NPI_M30_VENDEDOR } =
    CommissionMetricEnum;
const NEUTRAL_BPS = 10_000;

/** Ventas adicionales que se dibujan como mínimo. */
const LADDER_STEPS = 10;
/** El eje llega por lo menos a estas ventas, como el mock. */
export const LADDER_MIN_SALES = 16;
/** Hasta dónde se alarga para alcanzar la próxima meta. */
export const LADDER_MAX_STEPS = 30;

export type LadderUnlockKind = 'escalon' | 'meta' | 'multiplicador';

export interface LadderUnlock {
    ruleId: string;
    name: string;
    kind: LadderUnlockKind;
    /** `+$150 por pieza`, `+$500` o `×1.5`. */
    text: string;
}

export interface LadderPoint {
    /** Ventas del corte. */
    sales: number;
    /** Piezas del corte. */
    units: number;
    /** Lo que llevaría ganado en el corte. */
    cents: number;
    /** Lo que se activa con esta venta. */
    unlocks: LadderUnlock[];
}

interface LadderLeftOut {
    ruleId: string;
    name: string;
    reason: string;
}

export interface Ladder {
    /** Dónde va hoy. */
    now: LadderPoint;
    /** Una por cada venta adicional. */
    next: LadderPoint[];
    /** Reglas que pueden pagarle más pero no caben en la curva. */
    leftOut: LadderLeftOut[];
}

interface Counts {
    sales: number;
    units: number;
}

interface PerSaleRule {
    rule: RuleView;
    cents: number;
}

/** El motor compara igual: rangos inclusivos y listas exactas. */
function compare(condition: Condition, value: number): boolean {
    const target = condition.value;
    switch (condition.operator) {
        case CommissionOperatorEnum.GTE:
            return typeof target === 'number' && value >= target;
        case CommissionOperatorEnum.GT:
            return typeof target === 'number' && value > target;
        case CommissionOperatorEnum.LTE:
            return typeof target === 'number' && value <= target;
        case CommissionOperatorEnum.LT:
            return typeof target === 'number' && value < target;
        case CommissionOperatorEnum.BETWEEN:
            return (
                Array.isArray(target) &&
                target.length === 2 &&
                value >= target[0] &&
                value <= target[1]
            );
        case CommissionOperatorEnum.IN:
            return Array.isArray(target) && target.includes(value);
    }
}

const positionOf = (rule: RuleView, counts: Counts) =>
    countsSales(rule) ? counts.sales : counts.units;

const isPositionRule = (rule: RuleView) => rule.condition?.metric === POSICION_VENTA_EN_PERIODO;

/** ¿Se cumple la condición con esta posición? Las demás métricas se quedan como van hoy. */
function holdsAt(rule: RuleView, position: number, week: Week | null): boolean {
    const condition = rule.condition;
    if (!condition) return true;
    if (condition.metric === POSICION_VENTA_EN_PERIODO) return compare(condition, position);
    const { minSample } = condition;
    if (
        condition.metric === NPI_M30_VENDEDOR &&
        minSample !== null &&
        (week?.npiSample ?? 0) < minSample
    )
        return false;
    const value = metricNow(rule, condition.metric, week);
    return value !== null && compare(condition, value);
}

/** Umbrales y multiplicadores: sin piezas en el corte no dispara ninguno. */
const cutHolds = (rule: RuleView, counts: Counts, week: Week | null) =>
    counts.units > 0 && holdsAt(rule, positionOf(rule, counts), week);

const hasProductScope = (rule: RuleView) =>
    rule.scope.tipos.length > 0 || rule.scope.gamas.length > 0 || rule.scope.skus.length > 0;

/** Las reglas por venta de monto fijo y sin alcance de producto: las únicas que se pueden proyectar. */
function perSaleRules(rules: readonly RuleView[]): PerSaleRule[] {
    return rules.flatMap((rule) => {
        const bonus = rule.levers.bonoPorVenta;
        if (bonus?.mode !== CommissionBonusModeEnum.FIXED || hasProductScope(rule)) return [];
        return [{ rule, cents: bonus.value }];
    });
}

function leftOutOf(rules: readonly RuleView[], appliedNow: ReadonlySet<string>): LadderLeftOut[] {
    const left = new Map<string, LadderLeftOut>();
    for (const rule of rules) {
        const bonus = rule.levers.bonoPorVenta;
        const add = (reason: string) => {
            if (!left.has(rule.ruleId))
                left.set(rule.ruleId, { ruleId: rule.ruleId, name: rule.name, reason });
        };
        if (bonus && hasProductScope(rule)) add('depende de qué vendas');
        else if (bonus?.mode === CommissionBonusModeEnum.PERCENT) add('depende del precio');
        const metric = rule.condition?.metric;
        if (isCutRule(rule) && !appliedNow.has(rule.ruleId)) {
            if (metric === COLOCACION_PERIODO) add('depende del precio');
            if (metric === SKUS_DISTINTOS_PERIODO) add('depende de qué modelos vendas');
        }
    }
    return [...left.values()];
}

/** El factor combinado con el tope, como el motor: se multiplican y se redondea en cada paso. */
function combinedBps(factors: readonly number[], capBps: number | null): number {
    const bps = factors.reduce((acc, f) => Math.round((acc * f) / NEUTRAL_BPS), NEUTRAL_BPS);
    return capBps === null ? bps : Math.min(bps, capBps);
}

/** Los bonos por venta que cobra la venta en esta posición y el escalón que empieza ahí. */
function perSaleAt(
    perSale: readonly PerSaleRule[],
    counts: Counts,
    week: Week | null,
): { cents: number; unlocks: LadderUnlock[] } {
    let cents = 0;
    const unlocks: LadderUnlock[] = [];
    for (const { rule, cents: bonus } of perSale) {
        const position = positionOf(rule, counts);
        if (!holdsAt(rule, position, week)) continue;
        cents += bonus;
        if (isPositionRule(rule) && !holdsAt(rule, position - 1, week)) {
            const per = countsSales(rule) ? 'venta' : 'pieza';
            unlocks.push({
                ruleId: rule.ruleId,
                name: rule.name,
                kind: 'escalon',
                text: `+${pesos(bonus)} por ${per}`,
            });
        }
    }
    return { cents, unlocks };
}

function cutUnlocksOf(rule: RuleView): LadderUnlock[] {
    const { bonoUmbral, multiplicador } = rule.levers;
    const unlocks: LadderUnlock[] = [];
    const { ruleId, name } = rule;
    if (bonoUmbral) {
        unlocks.push({ ruleId, name, kind: 'meta', text: `+${pesos(bonoUmbral.amountCents)}` });
    }
    if (multiplicador) {
        unlocks.push({
            ruleId,
            name,
            kind: 'multiplicador',
            text: factor(multiplicador.factorBps),
        });
    }
    return unlocks;
}

/** Umbrales y multiplicadores del corte en curso: lo que el motor ya aplicó y lo que se evalúa. */
interface CutContext {
    rules: readonly RuleView[];
    week: Week | null;
    /** Reglas con umbral o multiplicador ya aplicado por el motor. */
    appliedNow: ReadonlySet<string>;
    /** Reglas que el front daría por cumplidas hoy. */
    heldNow: ReadonlySet<string>;
}

/**
 * ¿Paga la regla con estas ventas? Lo que ya aplicó el motor sigue aplicando salvo que una posición que
 * hoy se cumple deje de cumplirse (un tope "≤ 9"); lo que el front daría por cumplido y el motor no
 * pagó (una regla exclusiva, por ejemplo) no se activa solo.
 */
function firesAt(ctx: CutContext, rule: RuleView, counts: Counts): boolean {
    const held = ctx.heldNow.has(rule.ruleId);
    if (ctx.appliedNow.has(rule.ruleId)) {
        return !held || !isPositionRule(rule) || holdsAt(rule, positionOf(rule, counts), ctx.week);
    }
    return !held && cutHolds(rule, counts, ctx.week);
}

interface CutStep {
    fired: Set<string>;
    thresholdDelta: number;
    /** Los factores con esta venta; `null` si el multiplicador no cambia. */
    factors: number[] | null;
    unlocks: LadderUnlock[];
}

function cutAt(ctx: CutContext, counts: Counts, firedBefore: ReadonlySet<string>): CutStep {
    const fired = new Set(ctx.rules.filter((r) => firesAt(ctx, r, counts)).map((r) => r.ruleId));
    const ruleIds = new Set(ctx.rules.map((r) => r.ruleId));
    // Uno ya aplicado cuya regla ya no está vigente se queda como lo dejó el motor.
    const stillApplied = (ruleId: string) => !ruleIds.has(ruleId) || fired.has(ruleId);
    const thresholds = ctx.week?.thresholds ?? [];
    const multipliers = ctx.week?.multipliers ?? [];
    let thresholdDelta = thresholds
        .filter((t) => !stillApplied(t.ruleId))
        .reduce((sum, t) => sum - t.amountCents, 0);
    const kept = multipliers.filter((m) => stillApplied(m.ruleId)).map((m) => m.factorBps);
    const added: number[] = [];
    const unlocks: LadderUnlock[] = [];
    for (const rule of ctx.rules) {
        if (!fired.has(rule.ruleId)) continue;
        if (!ctx.appliedNow.has(rule.ruleId)) {
            thresholdDelta += rule.levers.bonoUmbral?.amountCents ?? 0;
            if (rule.levers.multiplicador) added.push(rule.levers.multiplicador.factorBps);
        }
        if (!firedBefore.has(rule.ruleId)) unlocks.push(...cutUnlocksOf(rule));
    }
    const changed = kept.length !== multipliers.length || added.length > 0;
    return { fired, thresholdDelta, factors: changed ? [...kept, ...added] : null, unlocks };
}

/**
 * La curva desde lo que lleva hoy: cada venta adicional suma sus bonos por venta en su posición, los
 * umbrales que cruza y el multiplicador que activa. Parte del neto real del corte, así que el primer
 * punto es exactamente lo que ya lleva.
 */
export function ladderOf(
    rules: readonly RuleView[],
    week: Week | null,
    capBps: number | null,
): Ladder {
    const base: Counts = { sales: week?.salesCount ?? 0, units: week?.unitsCount ?? 0 };
    const subtotal = week?.subtotalBonusCents ?? 0;
    const multiplierNow = week?.multiplierBps ?? NEUTRAL_BPS;
    const netNow = week?.netCents ?? 0;
    const multipliedNow = Math.round((subtotal * multiplierNow) / NEUTRAL_BPS);

    const perSale = perSaleRules(rules);
    const cutRules = rules.filter(isCutRule);
    // Lo que ya pagó hoy lo dice el motor; el front no lo vuelve a evaluar.
    const appliedNow = new Set([
        ...(week?.thresholds ?? []).map((t) => t.ruleId),
        ...(week?.multipliers ?? []).map((m) => m.ruleId),
    ]);
    const ctx: CutContext = {
        rules: cutRules,
        week,
        appliedNow,
        heldNow: new Set(cutRules.filter((r) => cutHolds(r, base, week)).map((r) => r.ruleId)),
    };

    const goal = nextGoalOf(rules, week);
    const toGoal = goal && goal.unit !== 'modelos' ? goal.missing : 0;
    const steps = Math.min(
        LADDER_MAX_STEPS,
        Math.max(LADDER_STEPS, LADDER_MIN_SALES - base.sales, toGoal),
    );

    const next: LadderPoint[] = [];
    let addedBonusCents = 0;
    let firedBefore: ReadonlySet<string> = appliedNow;
    for (let k = 1; k <= steps; k++) {
        const counts: Counts = { sales: base.sales + k, units: base.units + k };
        const sale = perSaleAt(perSale, counts, week);
        const cut = cutAt(ctx, counts, firedBefore);
        addedBonusCents += sale.cents;
        const multiplier = cut.factors ? combinedBps(cut.factors, capBps) : multiplierNow;
        const cents =
            netNow -
            multipliedNow +
            Math.round(((subtotal + addedBonusCents) * multiplier) / NEUTRAL_BPS) +
            cut.thresholdDelta;
        next.push({
            sales: counts.sales,
            units: counts.units,
            cents,
            unlocks: [...sale.unlocks, ...cut.unlocks],
        });
        firedBefore = cut.fired;
    }

    return {
        now: { sales: base.sales, units: base.units, cents: netNow, unlocks: [] },
        next,
        leftOut: leftOutOf(rules, appliedNow),
    };
}
